import { useState } from 'react';
import { ConfirmDialog } from '@components/primitives/ConfirmDialog';
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import { Box, Button, Typography } from '@mui/material';

import { cardSx, wf } from './tokens';

interface IntakeCompletionPageProps {
  onGoToDashboard?: () => void;
  /** Admin verification slot: only rendered for admins who may verify. */
  canVerifyIntake?: boolean;
  verifiedAt?: string | null;
  onVerifyIntake?: () => void | Promise<void>;
  onDiscardVerification?: () => void | Promise<void>;
  isVerifyingIntake?: boolean;
  isDiscardingVerification?: boolean;
  verifyBlockedReason?: string;
}

const VERIFY_EFFECTS =
  'Confirming marks this provider verified for the organization, feeds the organization progress and roster status, and delivers the intake verification event to downstream systems.';
const DISCARD_EFFECTS =
  'Discarding removes the verification marker, returns this provider to the submitted state on the organization roster and progress, and allows the intake to be verified again later. Systems already notified of the verification are not notified of the discard.';

export function IntakeCompletionPage({
  onGoToDashboard,
  canVerifyIntake = false,
  verifiedAt = null,
  onVerifyIntake,
  onDiscardVerification,
  isVerifyingIntake = false,
  isDiscardingVerification = false,
  verifyBlockedReason,
}: IntakeCompletionPageProps) {
  const [pendingAction, setPendingAction] = useState<'verify' | 'discard' | null>(null);
  const [isConfirming, setIsConfirming] = useState(false);
  const isVerified = Boolean(verifiedAt);
  const showAdminSlot =
    canVerifyIntake && (isVerified ? Boolean(onDiscardVerification) : Boolean(onVerifyIntake));

  // The dialog stays open until the mutation settles: closing on click would
  // unmount the loading state and re-enable both buttons mid-flight, which
  // invites a second submission of the same action.
  const confirmAction = async (action?: () => void | Promise<void>) => {
    if (isConfirming) return;
    setIsConfirming(true);
    try {
      await action?.();
    } finally {
      setIsConfirming(false);
      setPendingAction(null);
    }
  };

  return (
    <Box
      role="status"
      aria-labelledby="intake-completion-heading"
      sx={{
        width: '100%',
        maxWidth: '760px',
        mx: 'auto',
        pt: { xs: 3, sm: 7 },
        pb: 4,
        px: { xs: 0, sm: 2 },
        textAlign: 'center',
      }}
    >
      <Box
        sx={{
          width: 64,
          height: 64,
          mx: 'auto',
          mb: 2.5,
          borderRadius: '50%',
          bgcolor: wf.greenSoft,
          color: wf.green,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <CheckRoundedIcon aria-hidden="true" sx={{ fontSize: 36 }} />
      </Box>

      <Typography
        id="intake-completion-heading"
        component="h1"
        sx={{ color: wf.ink, fontSize: { xs: '24px', sm: '28px' }, fontWeight: 750 }}
      >
        Everything critical is in
      </Typography>
      <Typography
        sx={{
          color: wf.muted,
          fontSize: { xs: '15px', sm: '16px' },
          lineHeight: 1.45,
          mt: 1.75,
          mx: 'auto',
          maxWidth: '700px',
        }}
      >
        Our team reviews your items (typically 1–2 days), then starts filing with your payers. Payer
        review itself is set by the payers — 30 to 120+ days each; we&apos;ll show you exactly where
        things stand.
      </Typography>

      <Box
        sx={{
          ...cardSx,
          mt: 2.5,
          px: { xs: 2, sm: 2.5 },
          py: 2,
          textAlign: 'left',
        }}
      >
        <Typography component="h2" sx={{ color: wf.ink, fontSize: '17px', fontWeight: 700 }}>
          A few items can follow later
        </Typography>
        <Typography sx={{ color: wf.muted, fontSize: '15px', lineHeight: 1.45, mt: 0.75 }}>
          Professional references (before we file Kaiser), payer portal logins (only the ones we
          need), payment setup, and EHR access for migration. Find them under{' '}
          <Box component="strong" sx={{ fontWeight: 700 }}>
            Items we&apos;ll need later
          </Box>
          .
        </Typography>
      </Box>

      {showAdminSlot && (
        <Box sx={{ ...cardSx, mt: 2.5, px: { xs: 2, sm: 2.5 }, py: 2, textAlign: 'left' }}>
          <Typography component="h2" sx={{ color: wf.ink, fontSize: '17px', fontWeight: 700 }}>
            {isVerified ? 'This intake is verified' : 'Admin review'}
          </Typography>
          <Typography sx={{ color: wf.muted, fontSize: '15px', lineHeight: 1.45, mt: 0.75 }}>
            {isVerified
              ? 'This provider is marked verified for the organization. Discard the verification if it was recorded by mistake.'
              : 'All documents and fields have been submitted and are ready for Admin review.'}
          </Typography>
          {isVerified ? (
            <Button
              variant="outlined"
              color="warning"
              disabled={isDiscardingVerification || isConfirming}
              onClick={() => setPendingAction('discard')}
              sx={{ mt: 2, fontWeight: 700 }}
            >
              {isDiscardingVerification ? 'Discarding…' : 'Discard verification'}
            </Button>
          ) : (
            <Button
              variant="contained"
              disabled={isVerifyingIntake || isConfirming || Boolean(verifyBlockedReason)}
              onClick={() => setPendingAction('verify')}
              title={verifyBlockedReason}
              sx={{ mt: 2, fontWeight: 700 }}
            >
              {isVerifyingIntake ? 'Verifying…' : 'This all looks great — verify intake'}
            </Button>
          )}
          {verifyBlockedReason && !isVerified && (
            <Typography sx={{ color: wf.muted, fontSize: '12.5px', mt: 1 }}>
              {verifyBlockedReason}
            </Typography>
          )}
        </Box>
      )}

      <ConfirmDialog
        open={pendingAction === 'verify'}
        variant="info"
        title="Verify this intake?"
        message={VERIFY_EFFECTS}
        confirmText="Verify intake"
        loading={isVerifyingIntake || isConfirming}
        onClose={() => {
          if (isConfirming) return;
          setPendingAction(null);
        }}
        onConfirm={() => void confirmAction(onVerifyIntake)}
      />
      <ConfirmDialog
        open={pendingAction === 'discard'}
        variant="warning"
        title="Discard this verification?"
        message={DISCARD_EFFECTS}
        confirmText="Discard verification"
        loading={isDiscardingVerification || isConfirming}
        onClose={() => {
          if (isConfirming) return;
          setPendingAction(null);
        }}
        onConfirm={() => void confirmAction(onDiscardVerification)}
      />

      {onGoToDashboard ? (
        <Button
          variant="contained"
          size="large"
          onClick={onGoToDashboard}
          sx={{ mt: 2.5, minWidth: { xs: '100%', sm: 220 }, fontWeight: 700 }}
        >
          Go to your dashboard
        </Button>
      ) : (
        <Typography sx={{ color: wf.muted, fontSize: '13px', mt: 2.5 }}>
          This completion is recorded for the selected provider. Use the admin navigation to
          continue.
        </Typography>
      )}
    </Box>
  );
}
